import React, { FC, useContext, useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import ReleaseNoteForm, {
    ReleaseNoteFormValues,
    SUPPORTED_LANGUAGES,
    TranslationContentMap,
} from 'components/release-notes/release-note-form/release-note-form.component';
import {
    createReleaseNoteTemplate,
    getNextReleaseNoteVersionTemplate,
    getReleaseNotesTemplate,
} from 'apollo/templates/release-notes';
import { CreateReleaseNoteInput, ReleaseNote, ReleaseNoteOrganizationType } from 'models/release-note';

type Props = {
    open: boolean;
    onClose: () => void;
};

const validateTranslations = (translations: TranslationContentMap): string | undefined => {
    const hasEmptyTranslation = SUPPORTED_LANGUAGES.some(
        (language) => !(translations[language.code] || '').replace(/<[^>]*>/g, '').trim(),
    );
    if (hasEmptyTranslation) return 'ALL_TRANSLATIONS_REQUIRED';
    return undefined;
};

const CreateReleaseNoteDialog: FC<Props> = ({ open, onClose }) => {
    const { t: translate } = useTranslation(['release-notes', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);
    const [isTranslating, setIsTranslating] = useState(false);

    const { data: versionData } = useQuery<{ nextReleaseNoteVersion: string }>(getNextReleaseNoteVersionTemplate, {
        skip: !open,
        fetchPolicy: 'network-only',
    });

    const [createReleaseNote] = useMutation<{ createReleaseNote: ReleaseNote }>(createReleaseNoteTemplate, {
        onError: (error) => showBoundary(error),
        refetchQueries: [{ query: getReleaseNotesTemplate }],
    });

    const initialTranslations = SUPPORTED_LANGUAGES.reduce<TranslationContentMap>((accumulator, language) => {
        accumulator[language.code] = '';
        return accumulator;
    }, {});

    const formik = useFormik<ReleaseNoteFormValues>({
        initialValues: {
            organizationType: ReleaseNoteOrganizationType.all,
            translations: initialTranslations,
            isPublished: false,
        },
        validate: (values) => {
            const errors: Partial<Record<keyof ReleaseNoteFormValues, string>> = {};
            const contentError = validateTranslations(values.translations);
            if (contentError) errors.translations = translate(`release-notes:${contentError}`) as unknown as string;
            return errors;
        },
        validateOnChange: false,
        validateOnBlur: true,
        onSubmit: async (formValues, { resetForm }) => {
            const nextVersion = versionData?.nextReleaseNoteVersion ?? '1.0';
            const releaseNote: CreateReleaseNoteInput = {
                version: nextVersion,
                organizationType: formValues.organizationType,
                translations: SUPPORTED_LANGUAGES.filter((language) =>
                    formValues.translations[language.code]?.trim(),
                ).map((language) => ({
                    language: language.code,
                    content: formValues.translations[language.code],
                })),
            };

            await createReleaseNote({ variables: { releaseNote } });
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('release-notes:CREATE_SUCCESS'),
            });
            resetForm();
            onClose();
        },
    });

    const handleClose = () => {
        formik.resetForm();
        onClose();
    };

    const nextVersion = versionData?.nextReleaseNoteVersion ?? '...';

    return (
        <DHDialog
            open={open}
            onClose={handleClose}
            title={translate('release-notes:CREATE_RELEASE_NOTE')}
            maxWidth="md"
            fullWidth
            disableBackdropClick
            actions={
                <Box display="flex" gap={1.5}>
                    <Button variant="text" onClick={handleClose} disabled={formik.isSubmitting}>
                        {translate('common:CANCEL')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => formik.handleSubmit()}
                        disabled={formik.isSubmitting || isTranslating}
                        startIcon={formik.isSubmitting ? <CircularProgress size={16} /> : undefined}
                    >
                        {translate('release-notes:CREATE_RELEASE_NOTE')}
                    </Button>
                </Box>
            }
        >
            <Box pt={1}>
                <ReleaseNoteForm formik={formik} versionDisplay={nextVersion} onTranslatingChange={setIsTranslating} />
            </Box>
        </DHDialog>
    );
};

export default CreateReleaseNoteDialog;
